import React from 'react';
import PropTypes from 'prop-types';
import {history} from 'client/history';
import {resource} from '@albert-io/json-api-framework/request/builder';

import {genericMandarkRequest, invalidatePartialInterest} from 'resources/mandark.resource';
import {Set, List} from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {getModelForResourceType} from 'resources/modelRegistry';
import {
  addToast,
  copyToClipboard,
  WithTooltip,
  Dropdown,
  DropdownItem,
  SortableContainerContext,
  Icon,
  Modal,
  Dialogue,
  Text,
  Button,
  ListGroupItem,
  IconButton
} from '@albert-io/atomic';
import {pluralize} from 'lib/stringUtils';
import {
  getCurrentSort,
  clearSortValue
} from 'components/PracticeView/PracticeViewToolbar/Sort/sort.utils';
import {CreateAssignmentContext} from 'client/CreateAssignmentV2/CreateAssignmentProvider';

import Sort from 'components/PracticeView/PracticeViewToolbar/Sort/Sort.react';
import Filters from 'components/PracticeView/PracticeViewToolbar/Filters/Filters.react';
import PracticeViewToolbar from 'components/PracticeView/PracticeViewToolbar/PracticeViewToolbar.react';
import DuplicateFolderModal from 'client/Folders/DuplicateFolderModal/DuplicateFolderModal.react';
import UpgradeSubjectModal from 'components/PracticeView/UpgradeSubjectModal/UpgradeSubjectModal.react';
import {invalidateFilters} from 'components/PracticeView/PracticeViewToolbar/Filters/filters.utils';

import {getQuestionSetsQuery} from 'client/CreateAssignmentV2/utils/createAssignment.queries';
import RenameFolderModal from 'client/Folders/RenameFolderModal/RenameFolderModal.react';

import './folder-toolbar.scss';
import AssignModal, {SmartAssignTooltip} from 'components/AssignModal/AssignModal.react';
import track from 'react-tracking';

@track()
export default class FolderToolbar extends React.Component {
  static propTypes = {
    isOwner: PropTypes.bool,
    folder: PropTypes.instanceOf(getModelForResourceType('template_v1')),
    isSelectAllQuestionSetsMode: PropTypes.bool,
    clearQuestionSetSelections: PropTypes.func,
    toggleFoldersModal: PropTypes.func,
    triggerRerender: PropTypes.func,
    toggleRearranging: PropTypes.func,
    isRearranging: PropTypes.bool,
    questionSets: ImmutablePropTypes.listOf(
      PropTypes.instanceOf(getModelForResourceType('question_set_v1'))
    ),
    selectedQuestionSets: ImmutablePropTypes.set,
    isEmptyPracticeView: PropTypes.bool,
    invalidateQuestionsList: PropTypes.func,
    selectedQuestionCount: PropTypes.number,
    questionCount: PropTypes.number,
    isViewingFolderDetails: PropTypes.bool,
    resetQuestionsListOrder: PropTypes.func,
    getFilteredQuestionSetIds: PropTypes.func,
    isSorting: PropTypes.bool,
    isFiltering: PropTypes.bool,
    tracking: PropTypes.object,
    isLoading: PropTypes.bool
  };

  // needed for empty folders
  static defaultProps = {
    selectedQuestionSets: Set(),
    questionSets: List()
  };

  constructor() {
    super();
    this.state = {
      showDuplicateModal: false,
      showRenameModal: false,
      showCancelRearrangeModal: false,
      showDeleteQuestionsModal: false,
      showUpgradeModal: false,
      showSmartAssignModal: false
    };

    this.setHasSorted = () => {};
  }

  toggleState = (key) => {
    this.setState((state) => ({
      [key]: !state[key]
    }));
  };

  cancelRearranging = (onCancel = () => {}) => {
    this.props.toggleRearranging();
    this.setHasSorted(false);
    this.setState({
      showCancelRearrangeModal: false
    });
    onCancel();
  };

  copyToClipboard = () => {
    copyToClipboard(global.location.href, () => {
      addToast({
        type: 'success',
        title: 'Link Copied!',
        message: 'The link to this folder has been copied to your clipboard.'
      });
    });
  };

  handleDeleteQuestions = () => {
    this.deleteQuestions();
    this.toggleState('showDeleteQuestionsModal');
  };

  handleAssignFolder = () => {
    const selectedQuestionSetIds = this.props.selectedQuestionSets.toArray();
    const {bootstrapAssignment} = this.context;
    // if a user manually checks question sets, we directly pass all sets to the ACF
    // this is because we have already loaded and have access to all needed questions
    // else, a user is using `selectAll` mode and we don't have direct access to all sets due to pagination
    const bootstrapAssignmentObject = {
      assignmentOrigin: this.props.folder.getName(),
      resourceType: 'templates_v1',
      resourceId: this.props.folder.getId()
    };
    if (!this.props.isSelectAllQuestionSetsMode && !!selectedQuestionSetIds.length) {
      const allQuestionSets = this.props.questionSets;
      const selectedSets = allQuestionSets.filter((set) =>
        this.props.selectedQuestionSets.has(set.getId())
      );
      bootstrapAssignmentObject.questionSets = selectedSets;
    } else {
      bootstrapAssignmentObject.query = getQuestionSetsQuery(
        'templates_v1',
        this.props.folder.getId(),
        selectedQuestionSetIds
      );
    }
    bootstrapAssignment(bootstrapAssignmentObject);
    history.pushState(null, `/create-assignment/new-assignment`);
  };

  handleTracking = (content, options = {}) => {
    const {tracking} = this.props;
    tracking.trackEvent({
      event: 'click',
      feature: 'Folder Practice View',
      component: 'Assign dropdown',
      type: 'dropdown item',
      content,
      ...options
    });
  };

  handleSmartAssign = () => {
    const {folder} = this.props;

    this.handleTracking('Smart assign');
    const questionCount = folder.getMeta().getCountOfQuestions();
    const minSizeQuestionSet = folder.getMeta().getMinSizeQuestionSet();

    if (questionCount === 1) {
      this.handleAssignFolder();
    } else if (questionCount === minSizeQuestionSet) {
      this.handleAssignFolder();
    } else {
      this.toggleState('showSmartAssignModal');
    }
  };

  deleteQuestions = async () => {
    const {isFiltering} = this.props;

    let payload;
    if (isFiltering) {
      const filteredSetIds = await this.props.getFilteredQuestionSetIds();
      payload = {
        data: filteredSetIds?.map((questionSetId) => ({
          type: 'question_sets_v1',
          id: questionSetId
        }))
      };
    } else {
      payload = {
        data: this.props.selectedQuestionSets.map((questionSetId) => ({
          type: 'question_sets_v1',
          id: questionSetId
        }))
      };
    }

    // Select all mode without a filter has no selected question sets
    const hasQuestionsToDelete =
      payload.data || (this.props.isSelectAllQuestionSetsMode && !isFiltering);
    if (hasQuestionsToDelete) {
      try {
        if (this.props.isSelectAllQuestionSetsMode && !isFiltering) {
          await this.props.folder
            .replaceRelationship({
              type: 'question_sets_v1',
              relation: this.props.selectedQuestionSets
            })
            .save();
        } else {
          await genericMandarkRequest(
            'delete',
            {
              resourcePath: [
                'templates_v1',
                this.props.folder.getId(),
                'relationships',
                'question_sets_v1'
              ]
            },
            payload
          );
        }
        addToast({
          type: 'success',
          title: 'Success!',
          message: 'Your changes have been saved.'
        });
        invalidateFilters();
        invalidatePartialInterest(
          resource('templates_v1').mandarkEndpoint(['templates_v1']).done()
        );
        this.props.clearQuestionSetSelections();
        this.props.invalidateQuestionsList();
        this.props.triggerRerender();
      } catch (e) {
        addToast({
          type: 'danger',
          title: 'Error',
          message: 'Something went wrong with saving your changes.'
        });
      }
    }
  };

  saveReordering = async () => {
    const payload = {
      data: this.props.questionSets
        .map((questionSet, index) => {
          return {
            type: 'question_sets_v1',
            id: questionSet.getId(),
            meta: {
              position: index
            }
          };
        })
        .toArray()
    };
    try {
      await genericMandarkRequest(
        'post',
        {
          resourcePath: [
            'templates_v1',
            this.props.folder.getId(),
            'relationships',
            'question_sets_v1'
          ]
        },
        payload
      );
      addToast({
        type: 'success',
        title: 'Success!',
        message: 'Your changes have been saved.'
      });
      this.cancelRearranging();
      this.props.invalidateQuestionsList();
      invalidatePartialInterest(resource('templates_v1').mandarkEndpoint(['templates_v1']).done());
    } catch (e) {
      addToast({
        type: 'danger',
        title: 'Error',
        message: 'Something went wrong with saving your changes.'
      });
    }
  };

  getModalCollection = () => {
    const {folder, selectedQuestionCount, questionCount} = this.props;

    const deleteQuestionModalText =
      selectedQuestionCount === questionCount
        ? 'Remove all questions from the folder?'
        : `${selectedQuestionCount} ${pluralize(
            'question',
            selectedQuestionCount
          )} will be removed from this folder.`;
    return (
      <>
        {this.state.showCancelRearrangeModal && (
          <Modal
            ariaLabel='Cancel rerranging warning modal'
            handleClose={() => this.toggleState('showCancelRearrangeModal')}
          >
            {({modalContentStyle}) => {
              return (
                <Dialogue
                  inModal
                  handleClose={() => this.toggleState('showCancelRearrangeModal')}
                  className={modalContentStyle}
                >
                  <Dialogue.Title align='center'>
                    <Icon icon='exclamation-triangle' />
                  </Dialogue.Title>
                  <Dialogue.Body align='center'>
                    <Text>Unsaved changes will be lost.</Text>
                  </Dialogue.Body>
                  <Dialogue.BtnGroup fillColumn direction='column' align='center'>
                    <Button variant='text' onClick={this.saveReordering}>
                      Save changes
                    </Button>
                    <Button
                      onClick={() => this.cancelRearranging(this.props.resetQuestionsListOrder)}
                    >
                      Discard changes
                    </Button>
                  </Dialogue.BtnGroup>
                </Dialogue>
              );
            }}
          </Modal>
        )}
        {this.state.showDeleteQuestionsModal && (
          <Modal
            ariaLabel='Confirm question removal'
            handleClose={() => this.toggleState('showDeleteQuestionsModal')}
          >
            {({CloseButtonWrapper, modalContentStyle}) => {
              return (
                <Dialogue
                  inModal
                  hideCloseBtn
                  size='s'
                  handleClose={() => this.toggleState('showDeleteQuestionsModal')}
                  className={modalContentStyle}
                >
                  <Dialogue.Body align='left'>
                    <Text>{deleteQuestionModalText}</Text>
                  </Dialogue.Body>
                  <Dialogue.BtnGroup align='right'>
                    <CloseButtonWrapper flex>
                      <Button color='secondary'>Cancel</Button>
                    </CloseButtonWrapper>
                    <Button onClick={this.handleDeleteQuestions}>Remove</Button>
                  </Dialogue.BtnGroup>
                </Dialogue>
              );
            }}
          </Modal>
        )}
        {this.state.showDuplicateModal && (
          <DuplicateFolderModal
            folder={folder}
            handleClose={() => this.toggleState('showDuplicateModal')}
          />
        )}
        {this.state.showRenameModal && (
          <RenameFolderModal
            folder={folder}
            handleClose={() => this.toggleState('showRenameModal')}
            onSaveSuccess={this.props.triggerRerender}
          />
        )}
        {this.state.showUpgradeModal && (
          <UpgradeSubjectModal handleClose={() => this.toggleState('showUpgradeModal')} />
        )}
      </>
    );
  };

  renderSmartAssignModal = () => {
    const {folder} = this.props;
    const {showSmartAssignModal} = this.state;
    return (
      showSmartAssignModal && (
        <AssignModal
          resource={folder}
          type='folder'
          onClose={() => this.toggleState('showSmartAssignModal')}
        />
      )
    );
  };

  static contextType = CreateAssignmentContext;

  render() {
    const {
      isOwner,
      isSelectAllQuestionSetsMode,
      toggleFoldersModal,
      clearQuestionSetSelections,
      toggleRearranging,
      isRearranging,
      isEmptyPracticeView,
      isViewingFolderDetails,
      selectedQuestionCount,
      isSorting,
      isFiltering,
      isLoading
    } = this.props;
    const hasSelectedQuestions = !!selectedQuestionCount;
    if (isViewingFolderDetails) {
      return (
        <PracticeViewToolbar>
          <Button
            size='s'
            color='secondary'
            variant='text'
            className='u-mar-r_1'
            onClick={() => {
              this.handleAssignFolder();
              this.handleTracking('Assign');
            }}
          >
            Assign
          </Button>
          <Button
            size='s'
            color='secondary'
            variant='text'
            className='u-mar-r_1'
            onClick={() => this.toggleState('showDuplicateModal')}
          >
            Make a copy
          </Button>
          <Button
            size='s'
            color='secondary'
            variant='text'
            className='u-mar-r_1'
            onClick={this.copyToClipboard}
          >
            Copy share link
          </Button>
          {this.getModalCollection()}
        </PracticeViewToolbar>
      );
    }
    if (isEmptyPracticeView) {
      return (
        <PracticeViewToolbar>
          <Sort ignoredActions={['random']} active={isSorting} />
          <Filters
            active={isFiltering}
            disabled={hasSelectedQuestions}
            isLoading={isLoading}
            isFolder
          />
          {this.getModalCollection()}
        </PracticeViewToolbar>
      );
    }
    if (hasSelectedQuestions || isSelectAllQuestionSetsMode) {
      return (
        <PracticeViewToolbar>
          <Button size='s' className='u-mar-r_1' onClick={clearQuestionSetSelections}>
            Clear selection
          </Button>
          <Dropdown
            trigger={
              <Button
                size='s'
                className='u-mar-r_1'
                onClick={() =>
                  this.handleTracking('Assign', {type: 'dropdown', component: 'Toolbar'})
                }
              >
                Assign
                <Icon className='u-mar-l_1' icon='caret-down' />
              </Button>
            }
          >
            <DropdownItem
              as='button'
              onClick={() => {
                this.handleAssignFolder();
                this.handleTracking('Assign your selection');
              }}
            >
              Assign your selection ({selectedQuestionCount})
            </DropdownItem>
            <DropdownItem
              subtext='Any custom selections or filters will be ignored'
              as='button'
              onClick={this.handleSmartAssign}
            >
              <SmartAssignTooltip leftContent='Smart assign' />
            </DropdownItem>
          </Dropdown>
          <Button
            size='s'
            color='secondary'
            className='u-mar-r_1'
            variant='text'
            onClick={toggleFoldersModal}
          >
            Add to folder
          </Button>
          {isOwner && (
            <Button
              size='s'
              color='secondary'
              variant='text'
              className='u-mar-r_1'
              onClick={() => this.toggleState('showDeleteQuestionsModal')}
            >
              <Icon icon='trash' className='u-mar-r_1' />
              Remove
            </Button>
          )}
          {this.getModalCollection()}
          {this.renderSmartAssignModal()}
        </PracticeViewToolbar>
      );
    }
    if (!isOwner) {
      return (
        <PracticeViewToolbar>
          <Button
            size='s'
            onClick={() => {
              this.handleAssignFolder();
              this.handleTracking('Assign');
            }}
            className='u-mar-r_1'
          >
            Assign
          </Button>
          <Sort
            ignoredActions={['random']}
            active={isSorting}
            triggerButtonText='Order by'
            additionalDropdownItems={
              <ListGroupItem footer as={DropdownItem} onClick={clearSortValue}>
                <Text
                  bold
                  emphasis={getCurrentSort() === undefined ? 'high' : 'medium'}
                  color={getCurrentSort() === undefined ? 'blue' : 'black'}
                >
                  Default order
                </Text>
              </ListGroupItem>
            }
          />
          <Filters
            active={isFiltering}
            disabled={hasSelectedQuestions}
            isLoading={isLoading}
            isFolder
          />
          <PracticeViewToolbar.Divider />
          <Button
            size='s'
            color='secondary'
            variant='text'
            className='u-mar-r_1'
            onClick={this.copyToClipboard}
          >
            Copy share link
          </Button>
          <Button
            size='s'
            color='secondary'
            variant='text'
            className='u-mar-r_1'
            onClick={() => this.toggleState('showDuplicateModal')}
          >
            Make a copy
          </Button>
          {this.getModalCollection()}
        </PracticeViewToolbar>
      );
    }
    if (isRearranging) {
      return (
        <SortableContainerContext.Consumer>
          {(value) => {
            if (!value) {
              return null;
            }
            const {sortIndices, hasSorted, setHasSorted, cancelSortMode} = value;
            this.setHasSorted = setHasSorted;
            const hasStartedSorting = !sortIndices.isEmpty();

            return (
              <PracticeViewToolbar>
                <Button
                  size='s'
                  color='secondary'
                  variant='text'
                  className='u-mar-r_1'
                  onClick={
                    hasSorted
                      ? () => this.toggleState('showCancelRearrangeModal')
                      : () => this.cancelRearranging(cancelSortMode)
                  }
                >
                  <Icon icon='times' />
                </Button>
                <Button
                  size='s'
                  className='u-mar-r_1'
                  disabled={!hasSorted && !isSorting}
                  onClick={this.saveReordering}
                >
                  Save custom order
                </Button>
                {hasStartedSorting ? (
                  <Text size='m' className='u-mar-tb_auto u-mar-l_2'>
                    Click between questions to move a question.
                  </Text>
                ) : (
                  <>
                    <Text size='m' className='u-mar-tb_auto u-mar-l_1'>
                      Choose a question to move by clicking
                    </Text>
                    <IconButton
                      label='example reorder icon'
                      variant='solid'
                      color='primary'
                      size='s'
                      className='folder-toolbar__example-reorder u-mar-lr_1'
                      icon={['far', 'hand-pointer']}
                    />
                    <Text size='m'>below.</Text>
                  </>
                )}
                {this.getModalCollection()}
              </PracticeViewToolbar>
            );
          }}
        </SortableContainerContext.Consumer>
      );
    }
    // default view
    return (
      <PracticeViewToolbar>
        <Dropdown
          trigger={
            <Button
              size='s'
              className='u-mar-r_1'
              onClick={() =>
                this.handleTracking('Assign', {type: 'dropdown', component: 'Toolbar'})
              }
            >
              Assign
              <Icon icon='caret-down' className='u-mar-l_1' />
            </Button>
          }
        >
          <DropdownItem
            as='button'
            onClick={() => {
              this.handleAssignFolder();
              this.handleTracking('Assign all');
            }}
          >
            Assign all
          </DropdownItem>
          <DropdownItem
            subtext={isFiltering && 'Any custom selections or filters will be ignored'}
            as='button'
            onClick={this.handleSmartAssign}
          >
            <SmartAssignTooltip leftContent='Smart assign' />
          </DropdownItem>
        </Dropdown>
        <Sort
          active={isSorting}
          ignoredActions={['random']}
          triggerButtonText='Order by'
          additionalDropdownItems={
            <>
              <ListGroupItem as={DropdownItem} onClick={clearSortValue}>
                <Text
                  bold
                  emphasis={getCurrentSort() === undefined ? 'high' : 'medium'}
                  color={getCurrentSort() === undefined ? 'blue' : 'black'}
                >
                  Custom order
                </Text>
              </ListGroupItem>
              <WithTooltip
                placement='right'
                enabled={isFiltering}
                display='block'
                content='Clear all filters to edit.'
              >
                {/* @todo: give proper disabled state once dropdownItem is updated */}
                <ListGroupItem
                  footer
                  icon='pencil-alt'
                  as={DropdownItem}
                  onClick={!isFiltering ? toggleRearranging : () => {}}
                  disabled={isFiltering}
                >
                  Edit custom order
                </ListGroupItem>
              </WithTooltip>
            </>
          }
        />
        <Filters
          active={isFiltering}
          disabled={hasSelectedQuestions}
          isLoading={isLoading}
          isFolder
        />
        <PracticeViewToolbar.Divider />
        <Button
          onClick={this.copyToClipboard}
          size='s'
          color='secondary'
          variant='text'
          className='u-white-space_nowrap u-mar-r_1'
        >
          Copy share link
        </Button>
        <Dropdown
          trigger={
            <Button size='s' color='secondary' variant='text' className='u-mar-r_1'>
              More
              <Icon icon='caret-down' className='u-mar-l_1' />
            </Button>
          }
        >
          <DropdownItem onClick={() => this.toggleState('showRenameModal')}>Rename</DropdownItem>
          <DropdownItem onClick={() => this.toggleState('showDuplicateModal')}>
            Make copy
          </DropdownItem>
        </Dropdown>
        {this.getModalCollection()}
        {this.renderSmartAssignModal()}
      </PracticeViewToolbar>
    );
  }
}
